<!-- The chain, heading and all. Rendered where it stands in inline mode; in collapsible mode the
     same block is the expanding panel, and only the pill below it sits in the page. -->
<ng-template #chainPanel>
  <!-- Paints its own white rather than inheriting its host's: the info card sits on a tinted page
       and the bubbles' teal-50 only reads against white. The block is its own card, so it carries
       its own padding and corners rather than asking each host for them - except in collapsible
       mode, where the panel below already supplies both. Folded, it shrinks to the one-line row the
       heading needs, which is the padding the chrome transition runs between. Top padding is held at
       the folded state's so the heading doesn't move as it folds. -->
  <div class="w-full flex flex-col bg-white comments-chrome"
       [ngClass]="mode() !== 'inline' ? ''
         : showBody() ? 'pt-2.5 px-4 pb-8 rounded-[20px] border border-surface-200'
                      : 'px-4 py-2.5 rounded-[20px] border border-surface-200'"
       (transitionend)="onFoldSettled($event)">

    <div class="w-full flex items-center gap-3">
      <!-- comments-heading: the count and title start over the pill's and slide left into place.
           The strapline travels with them - it has no counterpart on the pill, so it fades in. -->
      <div class="flex items-center gap-3 comments-heading">
        <ta-comment-count [count]="commentCount()" [showZero]="true"></ta-comment-count>
        <b class="text-surface-700 font-semibold font-['Inter']" [ngClass]="mode() === 'collapsible' ? 'text-lg' :''">TA Comments</b>
        <span class="text-surface-500 font-normal font-['Inter'] comments-fade" [ngClass]="mode() === 'collapsible' ? 'text-sm' :'text-xs '">Have your say</span>
      </div>
      <div class="flex-1"></div>
      <!-- Same box as the pill's toggle, and the panel's top/right padding is the pill's less its
           border, so in collapsible mode it sits exactly where that one does, before, during and
           after the morph. Inline it is the section's own fold. -->
      <button type="button"
              class="shrink-0 w-5 h-5 rounded-full border border-surface-700 flex items-center justify-center text-[10px] text-surface-700 hover:bg-surface-100 cursor-pointer"
              [attr.aria-label]="showBody() ? 'Hide comments' : 'Show comments'"
              data-testid="ta-comments-collapse"
              (click)="mode() === 'collapsible' ? collapse() : toggleInline()">
        <i class="fa-regular fa-lg comments-chevron"
           [ngClass]="showBody() ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
      </button>
    </div>

    <!-- The fold: a grid row morphing between 0fr and 1fr, so there is no content height to measure
         and nothing to keep in step as the chain grows. Left at 1fr in collapsible mode, where the
         panel's own open animation carries the whole block and a second one would fight it.
         inert rather than @if: the composer and chain stay built across a fold - re-fetching a
         conversation to unfold it would be worse - but a folded block must not hold focus. -->
    <div class="grid comments-fold"
         [ngClass]="showBody() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'">
      <div class="min-h-0 overflow-hidden" [attr.inert]="showBody() ? null : ''">
        <!-- Collapsible makes up the panel's 16px: its right padding is only the pill's, to hold the toggle. -->
        <div class="w-full flex flex-col gap-4 pt-4" [ngClass]="mode() === 'collapsible' ? 'pr-[5px]' : ''">
          <!-- Composer. Enter sends and the send button stays for discoverability; Shift+Enter is the only
               way to a newline. Its draft is stored text - @mentions as tokens - not what is on screen.
               Dropped entirely, divider and all, where the host does not allow adding: a box that can
               only fail is worse than a chain that reads as read-only. -->
          @if (capabilities().adding) {
            <div class="w-full flex items-center gap-3 comments-fade">
              <div class="flex-1 min-w-0">
                <ta-comment-input #commentInput
                                  placeholder="Add your comment here. Use @ to mention people."
                                  [(value)]="newMessage"
                                  [people]="mentionPeople()"
                                  [peopleLoading]="mentionPeopleLoading()"
                                  [disabled]="!taId() || saving() || loading()"
                                  (focusChange)="onComposerFocus($event)"
                                  (submitted)="addComment()"></ta-comment-input>
              </div>
              <!-- preventDefault on mousedown keeps focus in the composer across the click: without it the
                   input blurs first, which both disables this button before the click lands and drops the
                   user out of the box they are still typing in. -->
              <button type="button"
                      aria-label="Add comment"
                      data-testid="ta-comment-send"
                      [disabled]="!canSend()"
                      (mousedown)="$event.preventDefault()"
                      (click)="addComment()"
                      class="shrink-0 w-9 h-9 pt-1 pr-1 rounded-full flex items-center justify-center leading-none text-base
                             text-white bg-primary-500 enabled:hover:bg-primary-600 disabled:bg-primary-300
                             cursor-pointer disabled:cursor-not-allowed">
                <i class="fa-solid fa-paper-plane"></i>
              </button>
            </div>

            <div class="w-full h-px bg-surface-200 comments-fade"></div>
          }

          <!-- The chain. Its height is measured off where it sits - see measureChain - so it takes
               whatever room is left down to near the bottom of the window, in the card and in the
               panel alike.
               The right inset is the scrollbar's lane, held open whether or not there is one to
               put in it: pr-3 clears an overlay bar, which is drawn over the content and would
               otherwise sit on the bubbles, and scrollbar-gutter keeps a bar that does take room
               from shunting the whole chain left the moment the comments outgrow the box. Still
               overflow-y-auto, so the bar itself only appears when there is something to scroll. -->
          <div #chain
               class="w-full flex flex-col gap-4 overflow-y-auto [scrollbar-gutter:stable] pr-4 pb-8 comments-fade [mask-image:linear-gradient(to_bottom,#000_calc(100%_-_2rem),transparent)]"
               [style.height]="chainHeight()">
            @if (loading() && !hasComments()) {
              <div class="w-full flex flex-col gap-3 items-center">
                <div class="text-surface-500 text-sm font-normal font-['Inter'] italic">Loading comments...</div>
                <taspinner class="self-center" size="sm"></taspinner>
              </div>
            }

            @for (comment of comments(); track comment.id ?? $index) {
              @if (isCommentRemoved(comment)) {
                <!-- Tombstone: the text is gone but the gap in the conversation still has to read, so
                     it keeps the side its comment was on. Others' takes the empty spacer, which holds
                     it in the same column as the bubbles that still have an avatar; your own has none
                     to stand in for, and runs to the right edge like the rest of yours. -->
                <div class="w-full flex gap-3 group">
                  @if (!isOwnComment(comment)) {
                    <div class="w-10 shrink-0"></div>
                  }
                  <div class="flex-1 min-w-0 flex flex-col gap-1"
                       [ngClass]="isOwnComment(comment) ? 'items-end' : 'items-start'">
                    <div class="text-surface-500 text-xs font-normal font-['Inter']">
                      {{ comment.deleteEvent?.created | date: 'dd/MM/yyyy  HH:mm' }}
                    </div>
                    <div class="px-4 py-1 min-h-12 rounded-2xl bg-surface-50 group-hover:bg-surface-100 transition-colors flex items-center gap-2 text-surface-700 text-xs font-normal font-['Inter'] italic leading-5">
                      <i class="fa-light fa-face-anguished"></i>
                      <span>Message deleted by its author.</span>
                    </div>
                  </div>
                </div>
              } @else {
                <!-- Everyone else's runs down the left behind their avatar; your own down the
                     right, unsigned - the side it is on is who it is from. The group is what the
                     controls and the bubble's own hover hang off. -->
                <div class="w-full flex gap-3 group">
                  @if (!isOwnComment(comment)) {
                    <!-- Initials only; who wrote it is the tooltip, so a wide chain stays readable.
                         Dropped to the bubble's top rather than the row's: the header line above it
                         is text-xs, a 16px line box, and the column sets them a gap-1 apart. -->
                    <div class="shrink-0 mt-5 w-10 h-10 rounded-full flex items-center justify-center bg-teal-400 text-white text-lg font-normal font-['Inter'] select-none"
                         [pTooltip]="commentAuthorTooltip(comment)"
                         tooltipStyleClass="ta-comment-user-tooltip"
                         tooltipPosition="left">
                      {{ commentInitials(comment) }}
                    </div>
                  }

                  <!-- items-* rather than stretched rows: a bubble is then as wide as its own text,
                       so a short message reads as a short bubble instead of a band across the chain.
                       Long text still wraps - fit-content caps an item at the column's width. -->
                  <div class="flex-1 min-w-0 flex flex-col gap-1"
                       [ngClass]="isOwnComment(comment) ? 'items-end' : 'items-start'">
                    <!-- Header and bubble shrink-wrapped together, so the header line can stretch to
                         whichever of the two is wider: on a long comment that is the bubble, and the
                         controls land on its left edge. The whole column while the editor is open -
                         the box wants the room, not the header's width. -->
                    <div class="max-w-full flex flex-col gap-1"
                         [ngClass]="[isOwnComment(comment) ? 'items-end' : 'items-start',
                                     isEditing(comment) ? 'w-full' : '']">
                      <!-- The line above the bubble: who wrote it - no name on your own, the side they
                           are on already says whose they are - what can be done to it, and when it was
                           sent. The controls sit here rather than under the bubble so a comment is one
                           block with its own header, and they read left to right into the stamp. -->
                      <div class="flex items-center gap-2 text-surface-500 text-xs font-normal font-['Inter'] mx-2"
                           [ngClass]="isOwnComment(comment) ? 'justify-end self-stretch' : ''">
                        @if (!isOwnComment(comment)) {
                          <span class="text-surface-500 font-semibold text-2xs">{{ commentAuthorName(comment) }}</span>
                        }
                        <!-- Each control is the host's to allow - see TaCommentCapabilities. A closed TA
                             allows neither, and the server refuses both from there, so offering them
                             would only produce a failed save.
                             Only under the pointer, or once tabbed into: they are the reader's own
                             comments and the chain is read far more often than it is edited. Faded
                             rather than removed, so the line keeps its width and the stamp beside them
                             does not slide as the pointer moves down the chain. mr-auto against the
                             line's justify-end puts them at its left edge - on a long comment, the
                             bubble's. -->
                        @if (showOwnCommentActions() && isOwnComment(comment) && !isEditing(comment)) {
                          <div class="flex items-center gap-3 mr-auto opacity-0 transition-opacity
                                      group-hover:opacity-100 focus-within:opacity-100">
                            @if (capabilities().deleting) {
                              <button type="button"
                                      class="flex items-center gap-1 text-2xs text-red-500 cursor-pointer disabled:opacity-50"
                                      data-testid="ta-comment-remove"
                                      [disabled]="saving()"
                                      (click)="removeComment(comment)">
                                <i class="fa-regular fa-trash-can"></i>Delete
                              </button>
                            }
                            @if (capabilities().editing) {
                              <button type="button"
                                      class="flex items-center gap-1 text-2xs text-slate-700 cursor-pointer disabled:opacity-50"
                                      data-testid="ta-comment-edit"
                                      [disabled]="saving()"
                                      (click)="startEdit(comment)">
                                <i class="fa-regular fa-pencil"></i>Edit
                              </button>
                            }
                          </div>
                        }
                        <!-- The editor's own pair, in the place the edit control was clicked from and
                             in its shape - the comment keeps one header whichever state it is in.
                             Always drawn, not hover-revealed: while the editor is open they are the
                             only way out of it. -->
                        @if (isEditing(comment)) {
                          <div class="flex items-center gap-3 mr-auto">
                            <button type="button"
                                    class="flex items-center gap-1 text-2xs text-primary-500 hover:text-primary-600 cursor-pointer disabled:opacity-50"
                                    data-testid="ta-comment-edit-save"
                                    [disabled]="!canSaveEdit()"
                                    (click)="saveEdit(comment)">
                              <i class="fa-regular" [ngClass]="saving() ? 'fa-spinner fa-spin' : 'fa-floppy-disk'"></i>Update
                            </button>
                            <button type="button"
                                    class="flex items-center gap-1 text-2xs text-surface-700 hover:text-surface-900 cursor-pointer disabled:opacity-50"
                                    data-testid="ta-comment-edit-cancel"
                                    [disabled]="saving()"
                                    (click)="cancelEdit()">
                              <i class="fa-regular fa-ban"></i>Cancel
                            </button>
                          </div>
                        }
                        <span class="text-2xs">
                          {{ (comment.editEvent ? 'Edited ' : '')
                             + ((comment.editEvent?.created ?? comment.creation?.created) | date: 'dd/MM/yyyy  HH:mm') }}
                        </span>
                      </div>

                      @if (isEditing(comment)) {
                        <!-- The composer's box again, so a mention can be added or removed on an edit.
                             Plain: the editor sits in the bubble column, where the space is worth more.
                             Its Update / Cancel are up in the header line - see above.
                             A workflow note keeps its tag as a pill above the box; it cannot be edited away. -->
                        <div class="self-stretch flex flex-col items-start gap-1">
                          @if (editingTag(); as key) {
                            <ta-workflow-tag [key]="key"/>
                          }
                          <ta-comment-input class="self-stretch"
                                            testId="ta-comment-edit-input"
                                            variant="plain"
                                            [(value)]="editDraft"
                                            [people]="mentionPeople()"
                                            [peopleLoading]="mentionPeopleLoading()"
                                            [rows]="2"
                                            [disabled]="saving()"
                                            (submitted)="saveEdit(comment)"></ta-comment-input>
                        </div>
                      } @else {
                        <!-- Square corner on the side it comes from. A step darker under the pointer,
                             off the row's hover, so it lights up with the controls above it.
                             48px tall for both a one-line comment and a two-line one: at 20px a
                             line, two lines and 4px either side come to exactly that, and below
                             that the min-height holds the box open and items-center puts the one
                             line in the middle of it. The third line is what grows the bubble. -->
                        <div class="px-4 py-1 min-h-12 flex items-center rounded-2xl text-xs font-normal font-['Inter'] leading-5 transition-colors"
                             [ngClass]="isOwnComment(comment)
                               ? 'rounded-br-none bg-teal-500 group-hover:bg-teal-600 text-white'
                               : 'rounded-bl-none bg-teal-50 group-hover:bg-[#E2FBF6] text-surface-700'">
                          <ta-comment-text [text]="comment.text"
                                           [tone]="isOwnComment(comment) ? 'own' : 'other'"></ta-comment-text>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }
            } @empty {
              @if (!loading()) {
                <div data-testid="ta-comments-empty"
                     class="text-surface-500 text-sm font-normal font-['Inter'] italic text-center rounded-2xl bg-surface-100 py-6 px-2">
                  {{ !!taId() ? 'No comments yet - be the first to add one!' : 'No TA selected.' }}
                </div>
              }
            }
          </div>
        </div>
      </div>
    </div>
  </div>
</ng-template>

@if (mode() === 'collapsible') {
  <!-- Always rendered, even while open: it is what the panel is measured against, and the panel
       covers it exactly, so the heading does not appear to move as the chain expands. -->
  <button #collapsedAnchor
          type="button"
          class="flex items-center gap-3 px-3 py-1.5 bg-white rounded-[20px]
                 shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)]
                 cursor-pointer hover:shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.30),0px_1px_3px_0px_rgba(0,0,0,0.30)] disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Show comments"
          data-testid="ta-comments-toggle"
          [disabled]="!taId()"
          (click)="expand()">
    <ta-comment-count [count]="commentCount()" [showZero]="true"></ta-comment-count>
    <b class="text-surface-700 text-lg font-semibold font-['Inter'] whitespace-nowrap">TA Comments</b>
    <span class="shrink-0 w-5 h-5 rounded-full border border-surface-700 flex items-center justify-center text-[10px] text-surface-700">
      <i class="fa-regular fa-chevron-down fa-lg"></i>
    </span>
  </button>

  @if (expanded()) {
    <!-- Fixed, not absolute: the layout's main column is overflow-hidden, so anything positioned
         inside the bread bar would be clipped by it rather than hanging over the page. -->
    <div class="fixed inset-0 z-40 bg-black/30"
         data-testid="ta-comments-backdrop"
         animate.enter="comments-backdrop-enter"
         animate.leave="comments-backdrop-leave"
         (click)="collapse()"></div>
    <!-- Top/right padding are the pill's py-1.5/px-3 less the 1px border, so the heading row and
         toggle sit on the pill's for the whole morph; only left/bottom padding grow - see
         ta-comments.scss. --comments-heading-slide carries the heading from the pill's position
         to this panel's; the keyframes read it. The chain is re-measured once the animation settles. -->
    <div class="fixed z-50 w-[38rem] max-w-[calc(100vw-2rem)] pt-[5px] pr-[11px] pb-4 pl-4 bg-white rounded-[20px] border border-surface-200 shadow-lg overflow-hidden"
         data-testid="ta-comments-panel"
         animate.enter="comments-panel-enter"
         animate.leave="comments-panel-leave"
         (animationend)="measure()"
         [style.--comments-heading-slide]="headingSlide()"
         [style.top.px]="panelTop()"
         [style.right.px]="panelRight()"
         [style.max-height]="panelMaxHeight()">
      <ng-container [ngTemplateOutlet]="chainPanel"></ng-container>
    </div>
  }
} @else {
  <ng-container [ngTemplateOutlet]="chainPanel"></ng-container>
}
